import { FC } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import HealthAndSafetyGrid from './healthAndSafetyGrid.view';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useHealthAndSafety } from '../../hooks/useHealthAndSafety';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyActions,
  EViewState,
} from '../../contexts/healthAndSafety.context';
import {
  THealthIncident,
  TSafetyProtocol,
  TEmergencyContact,
  THealthCheckup,
} from 'src/apis/administration/administration.interfaces';
import HealthIncidentsDetailsView from './subviews/details/healthIncidentsDetails.view';
import EmergencyContactsDetailsView from './subviews/details/emergencyContactsDetails.view';
import SafetyProtocolsDetailsView from './subviews/details/safetyProtocolsDetails.view';
import HealthCheckupDetailsView from './subviews/details/healthCheckupDetails.view';
import AddEditEmergencyContactsView from './subviews/manage/addEditEmergencyContacts.view';
import AddEditHealthCheckupView from './subviews/manage/addEditHealthCheckup.view';
import AddEditSafetyProtocolsView from './subviews/manage/addEditSafetyProtocols.view';
import AddEditHealthIncidentsView from './subviews/manage/addEditHealthIncidents.view';
import { AddButton, EditButton } from 'src/views/components/simple/AllButtons';

const HealthAndSafetyView: FC = () => {
  const { translateText } = useTranslation();
  const { incidents, protocols, emergencyContacts, healthCheckups, isLoading } =
    useHealthAndSafety();
  const {
    selectedIncident,
    selectedProtocol,
    selectedContact,
    selectedCheckup,
    viewState,
    currentViewType,
  } = useHealthAndSafetyState();
  const {
    selectIncident,
    selectProtocol,
    selectContact,
    selectCheckup,
    setViewState,
    setViewType,
  } = useHealthAndSafetyActions();

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="health-safety-loading">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.healthAndSafety.loading')}
        </Typography>
      </Box>
    );
  }

  // Check if data is loaded
  if (
    !incidents.length &&
    !protocols.length &&
    !emergencyContacts.length &&
    !healthCheckups.length
  ) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="health-safety-no-data">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.healthAndSafety.noData')}
        </Typography>
      </Box>
    );
  }

  // Calculate summary statistics
  const activeProtocols = protocols.filter(
    (protocol) => protocol.isActive
  ).length;
  const resolvedIncidents = incidents.filter(
    (incident) => incident.status === 'RESOLVED'
  ).length;
  const highPriorityContacts = emergencyContacts.filter(
    (contact) => contact.priority === 1
  ).length;
  const upcomingCheckups = healthCheckups.filter(
    (checkup) => checkup.nextCheckupDate && checkup.nextCheckupDate > new Date()
  ).length;
  const incidentsThisMonth = incidents.filter(
    (incident) =>
      incident.incidentDate &&
      incident.incidentDate.getMonth() === new Date().getMonth()
  ).length;

  const onIncidentsRowClick = (
    data: THealthIncident | TSafetyProtocol | TEmergencyContact | THealthCheckup
  ) => {
    selectIncident(data as THealthIncident);
    setViewType('incidents');
    setViewState(EViewState.DETAILS);
  };

  const onProtocolsRowClick = (
    data: THealthIncident | TSafetyProtocol | TEmergencyContact | THealthCheckup
  ) => {
    selectProtocol(data as TSafetyProtocol);
    setViewType('protocols');
    setViewState(EViewState.DETAILS);
  };

  const onContactsRowClick = (
    data: THealthIncident | TSafetyProtocol | TEmergencyContact | THealthCheckup
  ) => {
    selectContact(data as TEmergencyContact);
    setViewType('emergencyContacts');
    setViewState(EViewState.DETAILS);
  };

  const onCheckupsRowClick = (
    data: THealthIncident | TSafetyProtocol | TEmergencyContact | THealthCheckup
  ) => {
    selectCheckup(data as THealthCheckup);
    setViewType('checkups');
    setViewState(EViewState.DETAILS);
  };

  const handleBackToList = () => {
    setViewState(EViewState.LIST);
    setViewType(undefined);
    selectIncident(undefined);
    selectProtocol(undefined);
    selectContact(undefined);
    selectCheckup(undefined);
  };

  const renderDetailsView = () => {
    if (currentViewType === 'incidents' && selectedIncident) {
      return <HealthIncidentsDetailsView />;
    }
    if (currentViewType === 'protocols' && selectedProtocol) {
      return <SafetyProtocolsDetailsView />;
    }
    if (currentViewType === 'emergencyContacts' && selectedContact) {
      return <EmergencyContactsDetailsView />;
    }
    if (currentViewType === 'checkups' && selectedCheckup) {
      return <HealthCheckupDetailsView />;
    }
    return null;
  };

  const handleAddIncidentClickHandler = () => {
    setViewType('incidents');
    setViewState(EViewState.ADD);
    selectIncident(undefined);
    selectProtocol(undefined);
    selectContact(undefined);
    selectCheckup(undefined);
  };

  const handleAddProtocolClickHandler = () => {
    setViewType('protocols');
    setViewState(EViewState.ADD);
    selectIncident(undefined);
    selectProtocol(undefined);
    selectContact(undefined);
    selectCheckup(undefined);
  };

  const handleAddContactClickHandler = () => {
    setViewType('emergencyContacts');
    setViewState(EViewState.ADD);
    selectIncident(undefined);
    selectProtocol(undefined);
    selectContact(undefined);
    selectCheckup(undefined);
  };

  const handleAddCheckupClickHandler = () => {
    setViewType('checkups');
    setViewState(EViewState.ADD);
    selectIncident(undefined);
    selectProtocol(undefined);
    selectContact(undefined);
    selectCheckup(undefined);
  };

  const handleEditClickHandler = () => {
    setViewState(EViewState.EDIT);
    // Clear other selections based on current view type
    if (currentViewType === 'incidents') {
      selectProtocol(undefined);
      selectContact(undefined);
      selectCheckup(undefined);
    } else if (currentViewType === 'protocols') {
      selectIncident(undefined);
      selectContact(undefined);
      selectCheckup(undefined);
    } else if (currentViewType === 'emergencyContacts') {
      selectIncident(undefined);
      selectProtocol(undefined);
      selectCheckup(undefined);
    } else if (currentViewType === 'checkups') {
      selectIncident(undefined);
      selectProtocol(undefined);
      selectContact(undefined);
    }
  };

  const getEditLabel = () => {
    switch (currentViewType) {
      case 'incidents':
        return translateText(
          'administration.healthAndSafety.editHealthIncidents'
        );
      case 'protocols':
        return translateText(
          'administration.healthAndSafety.editSafetyProtocols'
        );
      case 'emergencyContacts':
        return translateText(
          'administration.healthAndSafety.editEmergencyContacts'
        );
      case 'checkups':
        return translateText(
          'administration.healthAndSafety.editHealthCheckup'
        );
      default:
        return translateText('common.edit');
    }
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto' }}
      data-testid="health-safety-container"
    >
      {viewState === EViewState.DETAILS || viewState === EViewState.EDIT ? (
        <Box
          sx={{
            mb: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
          }}
          data-testid="health-safety-details-header"
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{
              mb: { xs: 0, sm: 2 },
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="back-to-list-button"
          >
            {translateText('administration.healthAndSafety.backToList')}
          </Button>
          {viewState === EViewState.DETAILS && (
            <EditButton
              label={getEditLabel()}
              onClickHandler={handleEditClickHandler}
              data-testid="edit-button"
            />
          )}
        </Box>
      ) : null}

      {viewState === EViewState.DETAILS || viewState === EViewState.EDIT ? (
        <Box data-testid="health-safety-details-container">
          {renderDetailsView()}
        </Box>
      ) : (
        <>
          {/* Summary Statistics */}
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            sx={{ my: { xs: 2, sm: 4 } }}
            data-testid="health-safety-summary-stats"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'error.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-incidents-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {incidents.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.totalIncidents'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="active-protocols-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {activeProtocols}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.activeProtocols'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'success.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="resolved-incidents-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {resolvedIncidents}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.resolvedIncidents'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'warning.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="high-priority-contacts-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {highPriorityContacts}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.highPriorityContacts'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="upcoming-checkups-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {upcomingCheckups}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.upcomingCheckups'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'secondary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="monthly-incidents-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {incidentsThisMonth}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.thisMonthIncidents'
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            spacing={{ xs: 1, sm: 2, md: 3 }}
            data-testid="health-safety-grids-container"
          >
            <Grid item xs={12} data-testid="health-incidents-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.healthIncidents'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.healthAndSafety.addIncident'
                  )}
                  onClickHandler={handleAddIncidentClickHandler}
                  data-testid="add-incident-button"
                />
              </Box>
              <HealthAndSafetyGrid
                viewType="incidents"
                onRowClick={onIncidentsRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="safety-protocols-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.safetyProtocols'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.healthAndSafety.addProtocol'
                  )}
                  onClickHandler={handleAddProtocolClickHandler}
                  data-testid="add-protocol-button"
                />
              </Box>
              <HealthAndSafetyGrid
                viewType="protocols"
                onRowClick={onProtocolsRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="emergency-contacts-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.emergencyContacts'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.healthAndSafety.addContact'
                  )}
                  onClickHandler={handleAddContactClickHandler}
                  data-testid="add-contact-button"
                />
              </Box>
              <HealthAndSafetyGrid
                viewType="contacts"
                onRowClick={onContactsRowClick}
              />
            </Grid>

            <Grid item xs={12} data-testid="health-checkups-section">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'stretch', sm: 'center' },
                  mb: { xs: 1, sm: 2 },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                    mb: { xs: 0, sm: 1 },
                  }}
                >
                  {translateText(
                    'administration.healthAndSafety.healthCheckups'
                  )}
                </Typography>
                <AddButton
                  label={translateText(
                    'administration.healthAndSafety.addCheckup'
                  )}
                  onClickHandler={handleAddCheckupClickHandler}
                  data-testid="add-checkup-button"
                />
              </Box>
              <HealthAndSafetyGrid
                viewType="checkups"
                onRowClick={onCheckupsRowClick}
              />
            </Grid>
          </Grid>
        </>
      )}

      <AddEditEmergencyContactsView />
      <AddEditSafetyProtocolsView />
      <AddEditHealthCheckupView />
      <AddEditHealthIncidentsView />
    </Box>
  );
};

export default HealthAndSafetyView;
